<template>
    <!-- 1. Vista para páginas públicas (Pantalla limpia sin Sidebar, Topbar ni Footer) -->
    <div v-if="isPublicRoute" class="layout-wrapper p-4">
        <router-view />
    </div>

    <!-- 2. Vista del Panel Interno / Administración -->
    <div v-else :class="containerClass" @click="onDocumentClick">
        <div class="layout-wrapper-content">

            <AppTopBar
                :topbarMenuActive="topbarMenuActive"
                :profileMode="profileMode"
                :horizontal="layoutMode === 'horizontal'"
                :activeTopbarItem="activeTopbarItem"
                :layoutScheme="layoutScheme"
                @menubutton-click="onMenuButtonClick"
                @topbar-menubutton-click="onTopbarMenuButtonClick"
                @topbar-item-click="onTopbarItemClick"
                @layout-scheme-change="changeLayoutScheme"
            />

            <transition name="layout-menu-container">
                <div :class="menuClass" @click="onMenuClick" v-show="isMenuVisible()">
                    <div class="menu-scroll-content">

                        <AppInlineProfile
                            v-if="profileMode === 'inline' && layoutMode !== 'horizontal'"
                            @profile-click="onProfileClick"
                            :expanded="profileExpanded"
                        />

                        <AppMenu
                            v-if="getUser()" 
                            :model="filteredMenu"
                            :layoutMode="layoutMode"
                            :active="true" 
                            :mobileMenuActive="true" 
                            @menuitem-click="onMenuItemClick"
                            @root-menuitem-click="onRootMenuItemClick"
                        />

                    </div>
                </div>
            </transition>

            <div class="layout-main">
                <router-view />
            </div>

            <div class="layout-mask"></div>

            <AppFooter />

        </div>
    </div>
</template>

<script>
import AppTopBar from './AppTopbar.vue';
import AppInlineProfile from './AppInlineProfile.vue';
import AppMenu from './AppMenu.vue';
import AppFooter from './AppFooter.vue';
import EventBus from './event-bus';
import Auth from '@/mixins/Auth.vue';
import { useAuthStore } from './stores/authStore';
import { AuthService } from './service/AuthService';

export default {
    mixins: [Auth],
    props: {
        theme: String,
        layoutColor: String,
        layoutScheme: String,
        darkMenu: Boolean,
    },
    data() {
        return {
            layoutMode: 'static',
            profileMode: 'top',
            overlayMenuActive: false,
            staticMenuDesktopInactive: false,
            staticMenuMobileActive: false,
            topbarMenuActive: false,
            activeTopbarItem: null,
            rotateMenuButton: false,
            sessionInterval: null,
            displayTime: '00:00', 
            timerInterval: null,
            showExtendDialog: false, 
            
            menu : [
                {
                    label: 'Panel de Control',
                    items: [
                        { label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/dashboard' },
                    ]
                },
                {
                    permiso: 'menu-director',
                    label: 'Dirección',
                    items: [
                        { label: 'Docentes', icon: 'pi pi-fw pi-users', to: '/director/docentes' },
                        { label: 'Estudiantes', icon: 'pi pi-fw pi-user', to: '/director/estudiantes' },
                        { label: 'Bajo Rendimiento', icon: 'pi pi-fw pi-chart-line', to: '/director/centralizadorBR' },
                        { label: 'Calificaciones - Boletines', icon: 'pi pi-fw pi-file-pdf', to: '/director/boletines' },
                        { label: 'Centralizador Anual', icon: 'pi pi-fw pi-copy', to: '/director/centralizador-anual' },
                        { label: 'Mejores Estudiantes', icon: 'pi pi-fw pi-star', to: '/director/mejores-estudiantes' },
                        { label: 'Control', icon: 'pi pi-fw pi-sliders-h', to: '/director/control' },
                    ]
                },
                {
                    permiso: ['menu-secretaria','menu-director'], 
                    label: 'Secretaría',
                    items: [
                        { label: 'Inscripciones', icon: 'pi pi-fw pi-user-plus', to: '/secretaria/inscripciones' },
                        { label: 'Carga DDJJ Juancito Pinto', icon: 'pi pi-fw pi-upload', to: '/secretaria/declaraciones-juradas' },
                        { label: 'Consulta Pública DDJJ', icon: 'pi pi-fw pi-search', to: '/publico/declaraciones' }
                    ]
                },
                {
                    permiso: ['menu-docente'],
                    label: 'Docente',
                    items: [
                        { label: 'Control de Bajo Rendimiento', icon: 'pi pi-fw pi-exclamation-triangle', to: '/docente/cursos' },
                        { label: 'Registro Pedagógico', icon: 'pi pi-fw pi-book', to: '/docente/pedagogico' },
                        { label: 'Registro Calificaciones', icon: 'pi pi-fw pi-pencil', to: '/docente/calificaciones' },
                        { label: 'Centralizador de Área', icon: 'pi pi-fw pi-folder-open', to: '/docente/centralizador-trimestral' },
                    ]
                },
                {
                    permiso: ['menu-docente'],
                    label: 'Tutoria',
                    items: [
                        { label: 'Centralizador BR', icon: 'pi pi-fw pi-chart-bar', to: '/docente/centralizadorbr' },
                        { label: 'Centralizador Trimestral', icon: 'pi pi-fw pi-calendar', to: '/docente/centralizador-calificaciones' },
                        { label: 'Centralizador Anual', icon: 'pi pi-fw pi-calendar-plus', to: '/docente/centralizador-calificaciones-anual' },
                        { label: 'Mejores Estudiantes', icon: 'pi pi-fw pi-star', to: '/docente/mejores-estudiantes' },
                        { label: 'Boletín Electrónico', icon: 'pi pi-fw pi-envelope', to: '/docente/boletin-electronico' },
                    ]
                },
                {
                    permiso: ['menu-docente'],
                    label: 'Mis Datos',
                    items: [
                        { label: 'Mi cuenta', icon: 'pi pi-fw pi-id-card', to: '/usuario/perfil' },
                    ]
                },
                {
                    label: 'Administración',
                    permiso: 'Administrador', 
                    items: [
                        { label: 'Usuarios', icon: 'pi pi-fw pi-users', to: '/usuarios' },
                        { label: 'Roles', icon: 'pi pi-fw pi-shield', to: '/roles' },
                    ]
                }
            ],
            menuActive: true,
            profileExpanded: false,
            themeColors: [ {name: 'Blue', file: 'blue', color: '#0d6efd'} ],
            layoutColors: [ {name: 'Blue', file: 'blue', color: '#0d6efd'} ],
        }
    },
    computed: {
        // 🔥 DETECTA SI LA RUTA ACTUAL ES PÚBLICA (SIN MENÚ NI TOPBAR)
        isPublicRoute() {
            const publicNames = ['login', 'publico-declaraciones', 'notfound', 'error'];
            return publicNames.includes(this.$route.name);
        },

        authStore() {
            return useAuthStore();
        },
        filteredMenu() {
            const user = this.getUser();
            if (!user) return [];

            const result = this.menu
                .filter(section => {
                    if (user.roles?.includes('Administrador')) return true;
                    if (!section.permiso) return true;
                    if (Array.isArray(section.permiso)) {
                        return section.permiso.some(p => this.can(p));
                    }
                    return this.can(section.permiso);
                })
                .map(section => {
                    const newSection = { ...section };

                    if (section.items) {
                        newSection.items = section.items;
                        newSection.expanded = true;
                    }

                    return newSection;
                })
                .filter(section => section.items && section.items.length > 0);

            return result;
        },

        containerClass() {
            return ['layout-wrapper', {
                'menu-layout-static': this.layoutMode !== 'overlay',
                'menu-layout-overlay': this.layoutMode === 'overlay',
                'layout-menu-overlay-active': this.overlayMenuActive,
                'menu-layout-slim': this.layoutMode === 'slim',
                'menu-layout-horizontal': this.layoutMode === 'horizontal',
                'layout-menu-static-inactive': this.staticMenuDesktopInactive,
                'layout-menu-static-active': this.staticMenuMobileActive,
                'p-input-filled': this.$primevue.config.inputStyle === 'filled',
                'p-ripple-disabled': this.$primevue.config.ripple === false
            }];
        },
        menuClass() {
            return ['layout-menu-container', {'layout-menu-dark': this.darkMenu}];
        }
    },
    watch: {
        $route() {
            this.menuActive = false;
            this.$toast.removeAllGroups();
        }
    },
    methods: {
        checkSession() {
            const expiration = this.authStore.expiration;
            if (!expiration) return;

            const timeLeft = expiration - Date.now();

            if (timeLeft > 0 && timeLeft < 300000) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Sesión por expirar',
                    detail: 'Tu sesión terminará pronto. Guarda tus cambios.',
                    life: 5000
                });
            }
            
            if (timeLeft <= 0) {
                this.authStore.logout();
                this.$toast.add({
                    severity: 'error',
                    summary: 'Sesión Finalizada',
                    detail: 'Tu sesión ha expirado.',
                    life: 5000
                });
            }
        },
        updateTimer() {
            const expiration = this.authStore.expiration;
            if (!expiration) return;

            const now = Date.now();
            const diff = expiration - now;

            if (diff <= 0) {
                this.displayTime = '00:00';
                this.authStore.logout();
                return;
            }

            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            this.displayTime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

            if (minutes === 1 && seconds === 0) {
                this.$toast.add({
                    severity: 'info',
                    summary: 'Sesión por expirar',
                    detail: 'Tu sesión terminará en 1 minuto',
                    life: 5000
                });
            }
        },

        async renewSession() {
            const success = await this.authStore.extendSession(AuthService);
            if (success) {
                this.$toast.add({
                    severity: 'success',
                    summary: 'Sesión Extendida',
                    detail: 'Tu tiempo se ha renovado correctamente',
                    life: 3000
                });
            } else {
                this.authStore.logout();
            }
        },

        onDocumentClick() {
            if(!this.topbarItemClick) {
                this.activeTopbarItem = null;
                this.topbarMenuActive = false;
            }
            if(!this.menuClick) {
                if(this.isHorizontal() || this.isSlim()) {
                    this.menuActive = false;
                    EventBus.emit('reset-active-index');
                }
                this.hideOverlayMenu();
            }
            this.topbarItemClick = false;
            this.menuClick = false;
        },
        onProfileClick(event) {
            this.profileExpanded = !this.profileExpanded;
            if(this.isHorizontal() || this.isSlim()) {
                EventBus.emit('reset-active-index');
            }
            event.preventDefault();
        },
        onMenuClick() { this.menuClick = true; },
        onMenuItemClick(event) {
            if(!event.item.items) {
                EventBus.emit('reset-active-index');
                this.hideOverlayMenu();
            }
            if(!event.item.items && this.isHorizontal()) {
                this.menuActive = false;
            }
        },
        onRootMenuItemClick() { this.menuActive = !this.menuActive; },
        onMenuButtonClick(event) {
            this.menuClick = true;
            this.rotateMenuButton = !this.rotateMenuButton;
            this.topbarMenuActive = false;
            if(this.layoutMode === 'overlay') {
                this.overlayMenuActive = !this.overlayMenuActive;
            }
            else {
                if(this.isDesktop())
                    this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
                else
                    this.staticMenuMobileActive = !this.staticMenuMobileActive;
            }
            event.preventDefault();
        },
        onTopbarMenuButtonClick(event) {
            this.topbarItemClick = true;
            this.topbarMenuActive = !this.topbarMenuActive;
            this.hideOverlayMenu();
            event.preventDefault();
        },
        onTopbarItemClick(event) {
            this.topbarItemClick = true;
            if(this.activeTopbarItem === event.item)
                this.activeTopbarItem = null;
            else
                this.activeTopbarItem = event.item;
            event.originalEvent.preventDefault();
        },
        hideOverlayMenu() {
            this.rotateMenuButton = false;
            this.overlayMenuActive = false;
            this.staticMenuMobileActive = false;
        },
        isDesktop() { return window.innerWidth > 1024; },
        isHorizontal() { return this.layoutMode === 'horizontal'; },
        isSlim() { return this.layoutMode === 'slim'; },
        isMenuVisible() {
            if (this.isDesktop()) {
                if (this.layoutMode === 'static') return !this.staticMenuDesktopInactive;
                else if (this.layoutMode === 'overlay') return this.overlayMenuActive;
                else return true;
            } else {
                return this.staticMenuMobileActive || this.overlayMenuActive;
            }
        },
        onLayoutChange(layoutMode) {
            this.layoutMode = layoutMode;
            this.staticMenuDesktopInactive = false;
            this.overlayMenuActive = false;
            if (this.isHorizontal()) {
                this.profileMode = 'top';
            }
        },
        changeLayoutScheme(scheme) { this.$emit('layout-scheme-change', scheme); },
        onProfileModeChange(profileMode) { this.profileMode = profileMode; },
        onLayoutColorChange(layout) { this.$emit('layout-change', layout); },
        onThemeChange(theme) { this.$emit('theme-change', theme); }
    },
    components: {
        'AppTopBar': AppTopBar,
        'AppInlineProfile': AppInlineProfile,
        'AppMenu': AppMenu,
        'AppFooter': AppFooter
    },
    mounted() {
        if (typeof this.loadOptions === 'function') {
            this.loadOptions();
        }
        
        this.sessionInterval = setInterval(() => {
            this.checkSession();
        }, 60000);

        this.timerInterval = setInterval(() => {
            this.updateTimer();
        }, 1000);
    },
    beforeUnmount() {
        if (this.sessionInterval) {
            clearInterval(this.sessionInterval);
        }
        clearInterval(this.timerInterval);
    },
}
</script>

<style lang="scss">
@import './App.scss';
</style>